<template>
    <div class="projects-carousel">
        <div class="carousel-container" ref="carouselContainer">
            <button
                class="carousel-btn carousel-btn-prev"
                @click="scrollCarousel(-1)"
                :disabled="isAtStart"
                v-show="showArrows">
                <i class="fa fa-chevron-left"></i>
            </button>

            <div class="carousel-track" ref="carouselTrack" @scroll="handleScroll">
                <div
                    v-for="project in projects"
                    :key="project.id"
                    class="project-card">
                    <a :href="project.url" target="_blank" class="project-link">
                        <div class="project-image">
                            <img :src="project.img" :alt="project.enterprise">
                        </div>
                        <div class="project-info">
                            <h3 class="project-name">{{ project.enterprise }}</h3>
                            <p class="project-role">{{ project.role }}</p>
                        </div>
                    </a>
                </div>
            </div>

            <button
                class="carousel-btn carousel-btn-next"
                @click="scrollCarousel(1)"
                :disabled="isAtEnd"
                v-show="showArrows">
                <i class="fa fa-chevron-right"></i>
            </button>
        </div>

        <!-- Indicadores de navegación -->
        <div class="carousel-indicators" v-show="projects.length > visibleCards">
            <span
                v-for="(dot, index) in totalPages"
                :key="index"
                class="indicator-dot"
                :class="{ active: currentPage === index }"
                @click="scrollToPage(index)">
            </span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ProjectsCarousel',
    props: {
        projects: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            currentPage: 0,
            visibleCards: 3,
            isAtStart: true,
            isAtEnd: false,
            showArrows: false
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.projects.length / this.visibleCards);
        }
    },
    mounted() {
        this.updateVisibleCards();
        window.addEventListener('resize', this.updateVisibleCards);
        this.$nextTick(() => {
            this.handleScroll();
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateVisibleCards);
    },
    methods: {
        updateVisibleCards() {
            const width = window.innerWidth;
            if (width < 768) {
                this.visibleCards = 1;
            } else if (width < 1024) {
                this.visibleCards = 2;
            } else if (width < 1440) {
                this.visibleCards = 3;
            } else {
                this.visibleCards = 4;
            }
            this.showArrows = this.projects.length > this.visibleCards;
        },
        scrollCarousel(direction) {
            const track = this.$refs.carouselTrack;
            if (!track) return;

            const cardWidth = track.querySelector('.project-card')?.offsetWidth || 0;
            const gap = 30; // Gap entre cards
            const scrollAmount = (cardWidth + gap) * this.visibleCards;

            track.scrollBy({
                left: direction * scrollAmount,
                behavior: 'smooth'
            });
        },
        scrollToPage(pageIndex) {
            const track = this.$refs.carouselTrack;
            if (!track) return;

            const cardWidth = track.querySelector('.project-card')?.offsetWidth || 0;
            const gap = 30;
            const scrollAmount = (cardWidth + gap) * this.visibleCards * pageIndex;

            track.scrollTo({
                left: scrollAmount,
                behavior: 'smooth'
            });
        },
        handleScroll() {
            const track = this.$refs.carouselTrack;
            if (!track) return;

            const scrollLeft = track.scrollLeft;
            const maxScroll = track.scrollWidth - track.clientWidth;

            this.isAtStart = scrollLeft <= 10;
            this.isAtEnd = scrollLeft >= maxScroll - 10;

            // Calcular página actual
            const cardWidth = track.querySelector('.project-card')?.offsetWidth || 0;
            const gap = 30;
            const pageWidth = (cardWidth + gap) * this.visibleCards;
            this.currentPage = Math.round(scrollLeft / pageWidth);
        }
    }
}
</script>

<style scoped>
.projects-carousel {
    position: relative;
    width: 100%;
    margin: 0 auto;
}

.carousel-container {
    position: relative;
    display: flex;
    align-items: center;
    gap: 20px;
}

.carousel-track {
    display: flex;
    gap: 30px;
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 20px 0 60px 0;
    flex: 1;
}

.carousel-track::-webkit-scrollbar {
    display: none;
}

.project-card {
    flex: 0 0 calc((100% - 60px) / 3);
    width: calc((100% - 60px) / 3);
    min-width: 280px;
    max-width: calc((100% - 60px) / 3);
    background: #1a1d29;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
}

.project-card:hover {
    transform: translateY(-10px);
    background: #212430;
    border-color: rgba(255, 107, 107, 0.3);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.project-link {
    text-decoration: none;
    display: block;
    color: inherit;
}

.project-image {
    width: 100%;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    padding: 30px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.project-image img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: grayscale(100%);
    transition: filter 0.3s ease;
}

.project-card:hover .project-image img {
    filter: grayscale(0%);
}

.project-info {
    padding: 25px;
    text-align: center;
}

.project-name {
    font-size: 20px;
    font-weight: 700;
    color: #ff6b6b;
    margin: 0 0 10px 0;
    line-height: 1.3;
}

.project-role {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
    line-height: 1.5;
}

/* Botones del carrusel */
.carousel-btn {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #252834;
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    flex-shrink: 0;
    z-index: 2;
}

.carousel-btn:hover:not(:disabled) {
    background: #482d36;
    border-color: rgba(255, 107, 107, 0.4);
    transform: scale(1.1);
}

.carousel-btn:disabled {
    color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
}

.carousel-btn i {
    font-size: 18px;
}

/* Indicadores */
.carousel-indicators {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 30px;
}

.indicator-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #484a54;
    cursor: pointer;
    transition: all 0.3s ease;
}

.indicator-dot:hover {
    background: #8d444a;
}

.indicator-dot.active {
    background: #ff6b6b;
    width: 30px;
    border-radius: 5px;
}

/* Responsive */
@media (max-width: 1440px) {
    .project-card {
        flex: 0 0 calc((100% - 60px) / 3);
        width: calc((100% - 60px) / 3);
        max-width: calc((100% - 60px) / 3);
    }
}

@media (max-width: 1024px) {
    .project-card {
        flex: 0 0 calc((100% - 30px) / 2);
        width: calc((100% - 30px) / 2);
        max-width: calc((100% - 30px) / 2);
        min-width: 250px;
    }

    .project-image {
        height: 180px;
        padding: 25px;
    }

    .carousel-btn {
        width: 40px;
        height: 40px;
    }

    .carousel-btn i {
        font-size: 16px;
    }

    .carousel-track {
        padding: 20px 0 50px 0;
    }
}

@media (max-width: 768px) {
    .project-card {
        flex: 0 0 100%;
        width: 100%;
        max-width: 100%;
        min-width: 0;
    }

    .carousel-track {
        gap: 20px;
        padding: 20px 0 50px 0;
    }

    .carousel-container {
        gap: 10px;
    }

    .carousel-btn {
        width: 36px;
        height: 36px;
    }

    .carousel-btn i {
        font-size: 14px;
    }

    .project-image {
        height: 160px;
        padding: 20px;
    }

    .project-info {
        padding: 20px;
    }

    .project-name {
        font-size: 18px;
    }

    .project-role {
        font-size: 14px;
    }

    .carousel-indicators {
        margin-top: 20px;
    }
}

@media (max-width: 480px) {
    .carousel-btn {
        display: none;
    }

    .carousel-container {
        gap: 0;
    }

    .carousel-track {
        padding: 20px 0 40px 0;
    }

    .carousel-indicators {
        margin-top: 15px;
    }
}
</style>
